/**
 * Tracks "Added to Cart" events for Mixpanel
 * Captures when items are added to the cart with comprehensive product and cart data
 */

(function () {
  'use strict';

  // Store previous cart value to calculate "Cart Value Before"
  let previousCartValue = 0;
  let previousCartItems = [];
  let previousCartFullState = null; // Store full cart state for removal tracking
  let isInitialized = false;
  let isProcessingCartUpdate = false; // Prevent infinite loops
  let isProcessingMutation = false; // Prevent MutationObserver infinite loops

  // Express checkout elements (Shopify accelerated checkout). The wallet button
  // internals render in shadow DOM, but their click events are composed and
  // retarget to these host elements, so document-level delegation still works.
  const EXPRESS_WALLET_NAMES = {
    'shop-pay-wallet-button': 'shop_pay',
    'shopify-apple-pay-button': 'Apple Pay',
    'shopify-google-pay-button': 'Google Pay',
    'shopify-paypal-button': 'PayPal',
    'shopify-amazon-pay-button': 'Amazon Pay',
  };
  const EXPRESS_CHECKOUT_SELECTOR = Object.keys(EXPRESS_WALLET_NAMES)
    .concat([
      'shopify-accelerated-checkout',
      'shopify-accelerated-checkout-cart',
      'more-payment-options-link',
      '.shopify-payment-button__button',
    ])
    .join(', ');

  // Temporary QA logging for the express checkout tracking change — flip to
  // false (or strip the debugLog calls) before release
  const BEGIN_CHECKOUT_DEBUG = true;
  function debugLog(message, data) {
    if (BEGIN_CHECKOUT_DEBUG) {
      console.log('[Begin Checkout] ' + message, data !== undefined ? data : '');
    }
  }

  /**
   * Gets the current cart state
   */
  async function getCartState() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Collection membership ("Categories") resolver — Mixpanel Audit, Ticket 7.
  //
  // Neither the AJAX Cart API (/cart.js) nor /products/<handle>.js exposes a
  // product's collections, so we look them up through a tiny Liquid endpoint
  // (templates/product.mp-categories.liquid) that renders
  // `product.collections | map: 'handle'` as a JSON array. Results are cached in
  // memory + sessionStorage and keyed on the (language-stable) product handle, so
  // a product is fetched at most once per session regardless of how it was added
  // to the cart (PDP, quick-add, Algolia, bxgy gift, etc.). Matches the value
  // convention used by Product Viewed V.1.
  // ---------------------------------------------------------------------------
  const CATEGORY_CACHE_PREFIX = 'mp_categories_';
  const categoryPromiseCache = new Map(); // handle -> Promise<string[]>

  function resolveCategories(handle) {
    if (!handle) {
      return Promise.resolve([]);
    }
    if (categoryPromiseCache.has(handle)) {
      return categoryPromiseCache.get(handle);
    }

    // Reuse a value already resolved earlier this session
    try {
      const stored = sessionStorage.getItem(CATEGORY_CACHE_PREFIX + handle);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          const resolved = Promise.resolve(parsed);
          categoryPromiseCache.set(handle, resolved);
          return resolved;
        }
      }
    } catch (error) {
      // sessionStorage unavailable or malformed — fall through to fetch
    }

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}?view=mp-categories`;
    const request = fetch(url)
      .then((response) => {
        if (!response.ok) {
          // HTTP error — bail to the catch so we don't cache it, and a later
          // lookup can retry
          throw new Error('mp-categories request failed: ' + response.status);
        }
        return response.text();
      })
      .then((text) => {
        let parsed;
        try {
          parsed = JSON.parse(text);
        } catch (error) {
          // Body wasn't JSON — e.g. an HTML page served because the endpoint
          // template wasn't live yet. A single bad response must NOT poison the
          // session, so don't cache: drop the in-flight entry to allow a retry.
          categoryPromiseCache.delete(handle);
          return [];
        }
        if (!Array.isArray(parsed)) {
          categoryPromiseCache.delete(handle);
          return [];
        }
        // A genuine array (an empty one is a valid result for a product in no
        // published collections) — safe to cache for the rest of the session
        const handles = parsed.filter((value) => typeof value === 'string' && value);
        try {
          sessionStorage.setItem(CATEGORY_CACHE_PREFIX + handle, JSON.stringify(handles));
        } catch (error) {
          // sessionStorage full/unavailable — in-memory cache still applies
        }
        return handles;
      })
      .catch(() => {
        // Network/HTTP failure — don't cache, allow a later lookup to retry
        categoryPromiseCache.delete(handle);
        return [];
      });

    categoryPromiseCache.set(handle, request);
    return request;
  }

  // Deduped union of every line item's collection handles, for multi-item events
  async function resolveCategoriesForItems(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }
    const handleLists = await Promise.all(items.map((item) => resolveCategories(item && item.handle)));
    const union = new Set();
    handleLists.forEach((list) => list.forEach((handle) => union.add(handle)));
    return Array.from(union);
  }

  // Warm the cache ahead of time (fire-and-forget) so time-sensitive events like
  // Begin Checkout can read categories synchronously from cache before navigation.
  // Gated on the same conditions as the events themselves — there's no reason to
  // look anything up before Mixpanel is loaded (i.e. before OneTrust C0002 consent),
  // and bots are skipped entirely.
  function prewarmCategories(items) {
    if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
      return;
    }
    if (typeof isBot === 'function' && isBot()) {
      return;
    }
    if (Array.isArray(items)) {
      items.forEach((item) => resolveCategories(item && item.handle));
    }
  }

  /**
   * Extracts product data from cart item
   */
  function extractProductDataFromCartItem(item) {
    return {
      product_id: item.product_id?.toString() || null,
      variant_id: item.variant_id?.toString() || null,
      sku: item.sku || null,
      price: item.price || 0,
      brand: item.vendor || item.product_vendor || null,
      product_handle: item.handle || null, // canonical, language-stable product key
      product_title: item.product_title || item.title || null,
      quantity: item.quantity || 1,
    };
  }

  /**
   * Gets the most recently added or updated item from cart data
   * Compares with previous cart state to identify new items or quantity increases
   */
  async function getMostRecentItem(cartData) {
    // Get current cart state to compare
    const currentCart = await getCartState();
    if (!currentCart) {
      // If we can't get current cart, try to use cartData
      if (cartData && cartData.items && cartData.items.length > 0) {
        return cartData.items[cartData.items.length - 1];
      }
      return null;
    }

    // If current cart has items, find the newly added or updated one
    if (currentCart.items && currentCart.items.length > 0) {
      // Create a map of previous items by variant_id for quick lookup
      const previousItemsMap = new Map();
      previousCartItems.forEach((item) => {
        previousItemsMap.set(item.variant_id?.toString(), item.quantity || 0);
      });

      // Find items that are new (not in previous cart) or have increased quantity
      const addedOrUpdatedItems = currentCart.items.filter((item) => {
        const variantId = item.variant_id?.toString();
        const previousQuantity = previousItemsMap.get(variantId) || 0;
        const currentQuantity = item.quantity || 0;

        // Item is new or quantity increased
        return currentQuantity > previousQuantity;
      });

      // Return the first added/updated item (or last item as fallback)
      // Note: previousCartItems is updated AFTER tracking, not here
      if (addedOrUpdatedItems.length > 0) {
        return addedOrUpdatedItems[0];
      }

      // If no new/updated items found but cart has items, return the last item as fallback
      return currentCart.items[currentCart.items.length - 1];
    }

    // If cart is empty, return null (shouldn't happen if item was just added, but handle gracefully)
    return null;
  }

  /**
   * Tracks the "Added to Cart" event
   */
  async function trackAddedToCart(event) {
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      const cartData = event.detail?.cartData || event.cartData;
      if (!cartData) {
        return;
      }

      // Get the most recently added item
      const recentItem = await getMostRecentItem(cartData);
      if (!recentItem) {
        return;
      }

      // Extract product data
      const productData = extractProductDataFromCartItem(recentItem);

      // Get current cart state to get accurate total
      const currentCart = await getCartState();
      const cartValueAfter = currentCart?.total_price || cartData?.total_price || 0;

      // Use stored previous cart value for "before" (more accurate)
      // This is updated after each addition, so it represents the cart value before this addition
      const finalCartValueBefore = previousCartValue;

      // Collection handles of the added product (same convention as Product Viewed V.1)
      const categories = await resolveCategories(productData.product_handle);

      // Track the event
      mixpanel.track('Added to Cart V.1', {
        'Product ID': productData.product_id || '',
        'Product Handle': productData.product_handle || '', // canonical, language-stable key
        'Variant ID': productData.variant_id || '',
        SKU: productData.sku || '',
        Price: productData.price / 100, // Convert from cents to dollars
        Brand: productData.brand || '',
        'Product Title': productData.product_title || '',
        Categories: categories, // Shopify collection handles, matches Product Viewed V.1
        Quantity: productData.quantity || 1,
        'Cart Value Before': finalCartValueBefore / 100, // Convert from cents to dollars
        'Cart Value After': cartValueAfter / 100, // Convert from cents to dollars
        'User Agent': navigator.userAgent,
      });

      // Update previous cart value, items, and full state for next addition/removal
      previousCartValue = cartValueAfter;

      // Update previous cart full state and items
      const updatedCart = await getCartState();
      if (updatedCart) {
        previousCartFullState = updatedCart;
        previousCartItems = updatedCart.items.map((item) => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
        }));
      }
    } catch (error) {
      // Error tracking Added to Cart
    }
  }

  /**
   * Checks if items were actually added to the cart (new items or quantity increases)
   * Compares current cart state with stored previous cart state
   */
  async function checkIfItemsAdded() {
    const currentCart = await getCartState();
    if (!currentCart || !currentCart.items) {
      return false;
    }

    // If previous cart was empty and current cart has items, items were definitely added
    if (previousCartItems.length === 0 && currentCart.items.length > 0) {
      return true;
    }

    // Create a map of previous items by variant_id
    const previousItemsMap = new Map();
    previousCartItems.forEach((item) => {
      previousItemsMap.set(item.variant_id?.toString(), item.quantity || 0);
    });

    // Check if any items are new or have increased quantity
    const hasNewOrIncreasedItems = currentCart.items.some((item) => {
      const variantId = item.variant_id?.toString();
      const previousQuantity = previousItemsMap.get(variantId) || 0;
      const currentQuantity = item.quantity || 0;
      return currentQuantity > previousQuantity;
    });

    return hasNewOrIncreasedItems;
  }

  /**
   * Checks if items were actually removed from the cart
   * Compares current cart state with stored previous cart state
   */
  async function checkIfItemsRemoved() {
    const currentCart = await getCartState();

    // If previous cart had items and current cart is empty, items were definitely removed
    if (previousCartItems.length > 0 && (!currentCart || !currentCart.items || currentCart.items.length === 0)) {
      return true;
    }

    if (!currentCart || !currentCart.items) {
      return false;
    }

    // Create a map of current items by variant_id
    const currentItemsMap = new Map();
    currentCart.items.forEach((item) => {
      currentItemsMap.set(item.variant_id?.toString(), item.quantity || 0);
    });

    // Check if any items were removed or have decreased quantity
    const hasRemovedOrDecreasedItems = previousCartItems.some((prevItem) => {
      const variantId = prevItem.variant_id?.toString();
      const previousQuantity = prevItem.quantity || 0;
      const currentQuantity = currentItemsMap.get(variantId) || 0;
      return currentQuantity < previousQuantity;
    });

    return hasRemovedOrDecreasedItems;
  }

  /**
   * Gets the most recently removed item from cart
   * Compares with previous cart state to identify the removed item
   */
  async function getMostRecentRemovedItem() {
    // Get current cart state to compare
    const currentCart = await getCartState();
    if (!currentCart) {
      return null;
    }

    // If we don't have previous full cart state, can't determine what was removed
    if (!previousCartFullState || !previousCartFullState.items) {
      return null;
    }

    // Create a map of current items by variant_id
    const currentItemsMap = new Map();
    if (currentCart.items) {
      currentCart.items.forEach((item) => {
        currentItemsMap.set(item.variant_id?.toString(), item.quantity || 0);
      });
    }

    // Find items that were in previous cart but not in current, or have decreased quantity
    const removedItems = previousCartFullState.items.filter((prevItem) => {
      const variantId = prevItem.variant_id?.toString();
      const previousQuantity = prevItem.quantity || 0;
      const currentQuantity = currentItemsMap.get(variantId) || 0;
      return currentQuantity < previousQuantity;
    });

    // Return the first removed item with full data
    if (removedItems.length > 0) {
      // Calculate the quantity that was removed
      const removedItem = removedItems[0];
      const variantId = removedItem.variant_id?.toString();
      const previousQuantity = removedItem.quantity || 0;
      const currentQuantity = currentItemsMap.get(variantId) || 0;
      const quantityRemoved = previousQuantity - currentQuantity;

      // Return the item with the quantity that was actually removed
      return {
        ...removedItem,
        quantity: quantityRemoved, // The quantity that was removed, not the remaining quantity
      };
    }

    return null;
  }

  /**
   * Formats cart items for tracking
   */
  function formatCartItemsForTracking(items) {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    return items.map((item) => ({
      'Product Title': item.product_title || item.title || '',
      'Product Handle': item.handle || '', // canonical, language-stable key
      'Product ID': item.product_id?.toString() || '',
      'Variant ID': item.variant_id?.toString() || '',
      SKU: item.sku || '',
      Price: item.price ? item.price / 100 : 0, // Convert from cents to dollars
      Brand: item.vendor || item.product_vendor || '',
    }));
  }

  /**
   * Tracks the "Cart Drawer Viewed" event
   */
  async function trackCartDrawerViewed() {
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      // Get current cart state
      const cart = await getCartState();
      if (!cart) {
        return;
      }

      // Extract cart data
      const itemCount = cart.item_count || 0;
      const cartValue = cart.total_price ? cart.total_price / 100 : 0; // Convert from cents to dollars
      const currency = cart.currency || 'USD';
      const items = formatCartItemsForTracking(cart.items);

      // Warm the Categories cache so a subsequent Begin Checkout resolves instantly
      prewarmCategories(cart.items);

      // Track the event
      mixpanel.track('Cart Drawer Viewed V.1', {
        'Item Count': itemCount,
        'Cart Value': cartValue,
        Currency: currency,
        Items: items,
        'User Agent': navigator.userAgent,
      });
    } catch (error) {
      // Error tracking Cart Drawer Viewed
    }
  }

  /**
   * Tracks the "Removed from Cart" event
   */
  async function trackRemovedFromCart(event) {
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      const cartData = event.detail?.cartData || event.cartData;

      // Get the most recently removed item
      const removedItem = await getMostRecentRemovedItem();
      if (!removedItem) {
        return;
      }

      // Extract product data
      const productData = extractProductDataFromCartItem(removedItem);

      // Get current cart value (after removal)
      const currentCart = await getCartState();
      const cartValueAfter = currentCart?.total_price || cartData?.total_price || 0;

      // Use stored previous cart value for "before" (cart value before removal)
      const finalCartValueBefore = previousCartValue;

      // Track the event
      mixpanel.track('Removed from Cart V.1', {
        'Product ID': productData.product_id || '',
        'Product Handle': productData.product_handle || '', // canonical, language-stable key
        'Variant ID': productData.variant_id || '',
        SKU: productData.sku || '',
        Price: productData.price / 100, // Convert from cents to dollars
        Brand: productData.brand || '',
        'Product Title': productData.product_title || '',
        Quantity: productData.quantity || 1,
        'Cart Value Before': finalCartValueBefore / 100, // Convert from cents to dollars
        'Cart Value After': cartValueAfter / 100, // Convert from cents to dollars
        'User Agent': navigator.userAgent,
      });

      // Update previous cart value, items, and full state for next removal
      previousCartValue = cartValueAfter;
      if (currentCart) {
        previousCartFullState = currentCart;
        previousCartItems = currentCart.items.map((item) => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
        }));
      }
    } catch (error) {
      // Error tracking Removed from Cart
    }
  }

  /**
   * Tracks the "Begin Checkout" event
   */
  async function trackBeginCheckout(extraProperties) {
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        debugLog('SKIPPED — Mixpanel not loaded (OneTrust C0002 consent not granted yet?)');
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        debugLog('SKIPPED — bot detected');
        return;
      }

      // Get current cart state; fall back to the last known state in case the
      // fetch is cut short by the navigation to checkout
      const cart = (await getCartState()) || previousCartFullState;
      if (!cart) {
        debugLog('SKIPPED — no cart state available (fetch failed and no cached state)');
        return;
      }

      // Extract cart data (same as Cart Drawer Viewed)
      const itemCount = cart.item_count || 0;
      const cartValue = cart.total_price ? cart.total_price / 100 : 0; // Convert from cents to dollars
      const currency = cart.currency || 'USD';
      const items = formatCartItemsForTracking(cart.items);

      // Deduped union of every line item's collection handles. Usually resolves
      // instantly from the cache pre-warmed on cart update / drawer open.
      const categories = await resolveCategoriesForItems(cart.items);

      const properties = Object.assign(
        {
          'Item Count': itemCount,
          'Cart Value': cartValue,
          Currency: currency,
          Items: items,
          Categories: categories, // top-level union so `Categories contains <handle>` resolves
          'User Agent': navigator.userAgent,
        },
        extraProperties || {},
      );

      // Track the event; sendBeacon so it survives the navigation to checkout
      // (the default batched transport can strand the event in localStorage)
      debugLog('Tracking "Begin Checkout V.1" (cart payload)', properties);
      mixpanel.track('Begin Checkout V.1', properties, { transport: 'sendBeacon' });
      debugLog('mixpanel.track returned (cart payload)');
    } catch (error) {
      debugLog('THREW (cart payload) — event lost', error);
    }
  }

  /**
   * Resolves which express checkout control was clicked. Wallet buttons are
   * shadow DOM hosts, so the retargeted click lands on the host element.
   */
  function getExpressCheckoutType(target) {
    const wallet = target.closest(Object.keys(EXPRESS_WALLET_NAMES).join(', '));
    if (wallet) {
      return EXPRESS_WALLET_NAMES[wallet.tagName.toLowerCase()];
    }
    if (target.closest('more-payment-options-link')) {
      return 'More Payment Options';
    }
    // A click retargeted to the wrapper itself comes from its own shadow DOM,
    // which renders the fallback control (usually "Buy it now") when the
    // recommended wallet isn't available in this browser
    const wrapper = target.closest('shopify-accelerated-checkout, shopify-accelerated-checkout-cart');
    if (wrapper) {
      try {
        const fallback = JSON.parse(wrapper.getAttribute('fallback'));
        if (fallback && fallback.name === 'buy_it_now') {
          return 'Buy It Now';
        }
        if (fallback && fallback.name) {
          return fallback.name;
        }
      } catch (error) {
        // Missing or malformed fallback attribute
      }
    }
    return 'Express';
  }

  /**
   * A form's controls are exposed as named properties on the form element and
   * shadow its built-ins, so `form.id` returns the product form's
   * <input name="id"> (the variant input), not the form's id string
   */
  function getFormId(form) {
    return (form && form.getAttribute && form.getAttribute('id')) || '';
  }

  /**
   * Reads the section's ProductData JSON (rendered by main-product-new.liquid
   * for Mixpanel tracking) for the given product form, if present
   */
  function getProductDataForForm(form) {
    const sectionIdMatch = getFormId(form).match(/template--[^_\s]+__[\w-]+/);
    const script =
      (sectionIdMatch && document.getElementById('ProductData-' + sectionIdMatch[0])) ||
      document.querySelector('script[id^="ProductData-"]');
    if (!script) {
      return null;
    }
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * Looks up a variant in Shopify's analytics meta (present on product pages),
   * which has per-variant price and SKU even after variant switches
   */
  function findMetaVariant(variantId) {
    const metaProduct = window.ShopifyAnalytics?.meta?.product;
    if (!metaProduct || !Array.isArray(metaProduct.variants)) {
      return null;
    }
    return metaProduct.variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  /**
   * Tracks the "Begin Checkout" event for express checkout clicks inside a
   * product form. Buy-it-now style checkouts contain only the form's variant —
   * not the cart — so the payload is built synchronously from the form instead
   * of /cart.js (the browser may already be navigating away)
   */
  function trackBeginCheckoutFromProductForm(form, checkoutType) {
    debugLog('trackBeginCheckoutFromProductForm entered', {
      checkoutType: checkoutType,
      formId: getFormId(form),
      mixpanelPresent: typeof window.mixpanel !== 'undefined' && !!window.mixpanel.track,
    });
    try {
      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        debugLog('SKIPPED — Mixpanel not loaded (OneTrust C0002 consent not granted yet?)');
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        debugLog('SKIPPED — bot detected');
        return;
      }

      const variantInput = form.querySelector('[name="id"]');
      const variantId = variantInput ? variantInput.value : '';
      const formId = getFormId(form);
      const quantityInput =
        form.querySelector('[name="quantity"]') ||
        (formId ? document.querySelector('input[name="quantity"][form="' + formId + '"]') : null);
      const quantity = (quantityInput && parseInt(quantityInput.value, 10)) || 1;

      const productData = getProductDataForForm(form);
      const metaVariant = findMetaVariant(variantId);
      const productDataMatchesVariant = productData && String(productData.variant_id) === String(variantId);
      const sku = (metaVariant && metaVariant.sku) || (productDataMatchesVariant ? productData.sku : '') || '';
      const priceCents = (metaVariant && metaVariant.price) || (productDataMatchesVariant ? productData.price : 0) || 0;
      // ProductData JSON already carries the product's collection handles (same
      // source as Product Viewed V.1), so we avoid an async lookup on this
      // navigation-sensitive express path
      const categories = productData && Array.isArray(productData.categories) ? productData.categories : [];

      debugLog('PDP payload sources', {
        variantId: variantId,
        quantity: quantity,
        productDataScriptFound: !!productData,
        productDataMatchesVariant: !!productDataMatchesVariant,
        metaVariantFound: !!metaVariant,
      });

      const properties = {
        'Item Count': quantity,
        'Cart Value': (priceCents * quantity) / 100, // Convert from cents to dollars
        Currency: window.Shopify?.currency?.active || 'USD',
        Items: [
          {
            'Product Title': (productData && productData.product_title) || '',
            'Product ID': productData && productData.product_id != null ? String(productData.product_id) : '',
            'Variant ID': variantId ? String(variantId) : '',
            SKU: sku,
            Price: priceCents / 100, // Convert from cents to dollars
            Brand: (productData && productData.brand) || '',
          },
        ],
        Categories: categories, // single-product express checkout — its collection handles
        'User Agent': navigator.userAgent,
        'Checkout Type': checkoutType,
        'Checkout Source': 'product',
      };

      debugLog('Tracking "Begin Checkout V.1" (product form payload)', properties);
      mixpanel.track('Begin Checkout V.1', properties, { transport: 'sendBeacon' });
      debugLog('mixpanel.track returned (product form payload)');
    } catch (error) {
      debugLog('THREW (product form path) — event lost', error);
    }
  }

  /**
   * Handle cart update events (shared by both PUB_SUB and DOM events)
   */
  async function handleCartUpdateEvent(event) {
    // Prevent infinite loops: if we're already processing a cart update, ignore subsequent updates
    if (isProcessingCartUpdate) {
      return;
    }

    isProcessingCartUpdate = true;
    try {
      // Check if items were actually added (quantity increases or new items)
      const itemsAdded = await checkIfItemsAdded();
      if (itemsAdded) {
        await trackAddedToCart(event);
      } else {
        // Check if items were removed (quantity decreases or items removed)
        const itemsRemoved = await checkIfItemsRemoved();
        if (itemsRemoved) {
          await trackRemovedFromCart(event);
        }
      }
    } finally {
      isProcessingCartUpdate = false;
    }
  }

  /**
   * Initialize cart tracking
   */
  function initCartTracking() {
    // Subscribe to cart update events - track all cart updates
    if (typeof subscribe !== 'undefined' && window.PUB_SUB_EVENTS) {
      const cartUpdateUnsubscriber = subscribe(window.PUB_SUB_EVENTS.cartUpdate, handleCartUpdateEvent);

      // Store unsubscriber for cleanup if needed
      if (cartUpdateUnsubscriber) {
        window.mixpanelCartTrackingUnsubscriber = cartUpdateUnsubscriber;
      }
    }

    // Also listen to DOM events as fallback (but only if PUB_SUB is not available)
    // Note: We keep both for backwards compatibility, but handleCartUpdateEvent prevents duplicate processing
    document.addEventListener('cart:updated', handleCartUpdateEvent);

    // Listen for cart drawer open events (multiple methods for reliability)

    // Method 1: Listen to the custom event if it's dispatched
    document.addEventListener('cart-drawer:open', () => {

      console.log('cart-drawer:open');
      trackCartDrawerViewed();
    });

    // Method 2: Watch for class changes on the cart drawer element
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Use MutationObserver to watch for 'active' class being added
      const observer = new MutationObserver((mutations) => {
        // Prevent infinite loops from classList modifications
        if (isProcessingMutation) {
          return;
        }

        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.classList.contains('active') && !target.classList.contains('mixpanel-tracked-open')) {
              // Temporarily disconnect observer to prevent infinite loop
              observer.disconnect();
              isProcessingMutation = true;
              
              // Mark as tracked to avoid duplicate events
              target.classList.add('mixpanel-tracked-open');
              trackCartDrawerViewed();
              
              // Reconnect observer after a brief delay
              setTimeout(() => {
                observer.observe(cartDrawer, {
                  attributes: true,
                  attributeFilter: ['class'],
                });
                isProcessingMutation = false;
              }, 0);
            } else if (!target.classList.contains('active') && target.classList.contains('mixpanel-tracked-open')) {
              // Temporarily disconnect observer to prevent infinite loop
              observer.disconnect();
              isProcessingMutation = true;
              
              // Only remove if it's actually present to avoid unnecessary mutations
              target.classList.remove('mixpanel-tracked-open');
              
              // Reconnect observer after a brief delay
              setTimeout(() => {
                observer.observe(cartDrawer, {
                  attributes: true,
                  attributeFilter: ['class'],
                });
                isProcessingMutation = false;
              }, 0);
            }
          }
        });
      });

      // Start observing the cart drawer for class changes
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    } else {
      // If cart drawer doesn't exist yet, wait for it
      const checkForCartDrawer = setInterval(() => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          clearInterval(checkForCartDrawer);
          // Set up observer on the found drawer
          const observer = new MutationObserver((mutations) => {
            // Prevent infinite loops from classList modifications
            if (isProcessingMutation) {
              return;
            }

            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const target = mutation.target;
                if (target.classList.contains('active') && !target.classList.contains('mixpanel-tracked-open')) {
                  // Temporarily disconnect observer to prevent infinite loop
                  observer.disconnect();
                  isProcessingMutation = true;
                  
                  target.classList.add('mixpanel-tracked-open');
                  trackCartDrawerViewed();
                  
                  // Reconnect observer after a brief delay
                  setTimeout(() => {
                    observer.observe(drawer, {
                      attributes: true,
                      attributeFilter: ['class'],
                    });
                    isProcessingMutation = false;
                  }, 0);
                } else if (!target.classList.contains('active') && target.classList.contains('mixpanel-tracked-open')) {
                  // Temporarily disconnect observer to prevent infinite loop
                  observer.disconnect();
                  isProcessingMutation = true;
                  
                  // Only remove if it's actually present to avoid unnecessary mutations
                  target.classList.remove('mixpanel-tracked-open');
                  
                  // Reconnect observer after a brief delay
                  setTimeout(() => {
                    observer.observe(drawer, {
                      attributes: true,
                      attributeFilter: ['class'],
                    });
                    isProcessingMutation = false;
                  }, 0);
                }
              }
            });
          });
          observer.observe(drawer, {
            attributes: true,
            attributeFilter: ['class'],
          });
        }
      }, 500);

      // Stop checking after 10 seconds
      setTimeout(() => clearInterval(checkForCartDrawer), 10000);
    }

    // Listen for checkout button clicks
    // Use event delegation to catch all checkout buttons (cart drawer, main cart, etc.)
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const button = target.closest('button');

        // Check if the clicked element is a checkout button
        const isCheckoutButton =
          target.id === 'CartDrawer-Checkout' ||
          target.id === 'checkout' ||
          (button && button.id === 'CartDrawer-Checkout') ||
          (button && button.id === 'checkout') ||
          (button && button.name === 'checkout') ||
          (button && button.getAttribute('form') && button.name === 'checkout');

        if (isCheckoutButton) {
          const isCartDrawer = target.id === 'CartDrawer-Checkout' || (button && button.id === 'CartDrawer-Checkout');
          debugLog('Standard checkout button clicked', {
            source: isCartDrawer ? 'cart-drawer' : 'cart',
            clickTarget: target.tagName.toLowerCase() + (target.id ? '#' + target.id : ''),
          });
          trackBeginCheckout({
            'Checkout Type': 'Standard',
            'Checkout Source': isCartDrawer ? 'cart-drawer' : 'cart',
          });
          return;
        }

        // Express checkout buttons (Shop Pay, Apple Pay, Google Pay, PayPal,
        // Buy it now) never match the IDs above — catch them here
        const expressEl = target.closest(EXPRESS_CHECKOUT_SELECTOR);
        if (expressEl) {
          const checkoutType = getExpressCheckoutType(target);
          const productForm = expressEl.closest('form[action*="/cart/add"]');
          debugLog('Express checkout element clicked', {
            matchedElement: expressEl.tagName.toLowerCase(),
            clickTarget: target.tagName.toLowerCase(),
            checkoutType: checkoutType,
            inProductForm: !!productForm,
          });
          if (productForm) {
            trackBeginCheckoutFromProductForm(productForm, checkoutType);
          } else {
            trackBeginCheckout({ 'Checkout Type': checkoutType, 'Checkout Source': 'cart' });
          }
        }
      },
      true,
    ); // Use capture phase to catch early

    // Initialize previous cart value, items, and full state
    getCartState().then((cart) => {
      if (cart) {
        previousCartValue = cart.total_price || 0;
        previousCartItems = cart.items.map((item) => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
        }));
        previousCartFullState = cart; // Store full cart state for removal tracking
        isInitialized = true;

        // Warm the Categories cache for any items already in the cart at load,
        // so a Begin Checkout straight from the cart page resolves instantly
        prewarmCategories(cart.items);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartTracking);
  } else {
    initCartTracking();
  }
})();
